/*
 * Copyright (c) 2025. World Wide Generation Ltd
 */

import { useCallback, useLayoutEffect, useRef } from 'react';
import { Doc } from 'yjs';
import { HocuspocusProvider, HocuspocusProviderWebsocket } from '@hocuspocus/provider';
import config from '../../config';

// Lexical's CollaborationPlugin calls connect()/disconnect(); the shared websocket owns the connection.
class LexicalHocuspocusProvider extends HocuspocusProvider {
  constructor(...args: ConstructorParameters<typeof HocuspocusProvider>) {
    super(...args);
    // Lexical bootstraps the editor on 'sync', which Hocuspocus calls 'synced'
    this.on('synced', (data: { state: boolean }) => this.emit('sync', data.state));
  }

  override async connect() {}
  override disconnect() {}
}

interface ColabState {
  ws: HocuspocusProviderWebsocket,
  providerMap: Map<string, HocuspocusProvider>
}

const createWebSocketConn = (): ColabState => {
  return {
    ws: new HocuspocusProviderWebsocket({
      url: `${config.websocketUrl}/ws/collaboration`
    }),
    providerMap: new Map(),
  };
}

const getDocFromMap = (id: string, yjsDocMap: Map<string, Doc>): Doc => {
  const doc = yjsDocMap.get(id);
  if (doc) {
    doc.load();
    return doc
  }

  const newDoc = new Doc();
  yjsDocMap.set(id, newDoc);

  return newDoc;
};

export const useProviderFactory = () => {

  const ref = useRef<ColabState>();

  useLayoutEffect(() => {
    if (!ref.current) {
      ref.current = createWebSocketConn()
    }
    return () => {
      ref.current?.providerMap.forEach((provider) => provider.destroy());
      ref.current?.ws.destroy();
      ref.current = undefined;
    }
  }, []);


  const providerFactory = useCallback((id: string, yjsDocMap: Map<string, Doc>) => {
    if (!ref.current) {
      ref.current = createWebSocketConn();
    }

    const existingProvider = ref.current.providerMap.get(id);
    if (existingProvider) {
      // Each editor mount has its own yjsDocMap; bind it to the doc the provider already syncs
      yjsDocMap.set(id, existingProvider.document);
      return existingProvider;
    }

    const provider = new LexicalHocuspocusProvider({
      websocketProvider: ref.current.ws,
      name: id,
      document: getDocFromMap(id, yjsDocMap),
    });
    // In v3, we need to explicitly attach when using multiplexing
    provider.attach();

    ref.current.providerMap.set(id, provider);
    return provider as any;
  }, []);

  return { providerFactory };
}
